import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  X,
  Pen,
  Highlighter,
  ArrowRight,
  Square,
  Circle,
  Type,
  Undo,
  Trash2,
  Save,
  Download,
  Palette,
} from 'lucide-react';
import { toast } from 'sonner';
import { PhotoAnnotationCanvas, DrawingTool } from '../utils/photo/annotation';

interface PhotoAnnotatorProps {
  imageUrl: string;
  onSave: (annotatedImage: string, annotations: any[]) => void;
  onClose: () => void;
}

export default function PhotoAnnotator({ imageUrl, onSave, onClose }: PhotoAnnotatorProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [annotator, setAnnotator] = useState<PhotoAnnotationCanvas | null>(null);
  const [currentTool, setCurrentTool] = useState<DrawingTool['type']>('pen');
  const [currentColor, setCurrentColor] = useState('#FF0000');
  const [currentSize, setCurrentSize] = useState(3);
  const [showColorPicker, setShowColorPicker] = useState(false);

  const colors = [
    '#FF0000', // Red
    '#00FF00', // Green
    '#0000FF', // Blue
    '#FFFF00', // Yellow
    '#FF00FF', // Magenta
    '#00FFFF', // Cyan
    '#FFA500', // Orange
    '#800080', // Purple
    '#FFFFFF', // White
    '#000000', // Black
  ];

  useEffect(() => {
    if (!canvasRef.current) return;

    // Load image
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = canvasRef.current!;
      const container = containerRef.current!;
      
      // Set canvas size to match container
      const containerRect = container.getBoundingClientRect();
      canvas.width = containerRect.width;
      canvas.height = containerRect.height;

      // Draw image scaled to fit
      const ctx = canvas.getContext('2d')!;
      const scale = Math.min(
        canvas.width / img.width,
        canvas.height / img.height
      );
      const x = (canvas.width - img.width * scale) / 2;
      const y = (canvas.height - img.height * scale) / 2;
      
      ctx.drawImage(img, x, y, img.width * scale, img.height * scale);

      // Initialize annotator
      const newAnnotator = new PhotoAnnotationCanvas(canvas);
      setAnnotator(newAnnotator);
    };

    img.onerror = () => {
      toast.error('Failed to load image');
    };

    img.src = imageUrl;

    return () => {
      if (annotator) {
        annotator.destroy();
      }
    };
  }, [imageUrl]);

  useEffect(() => {
    if (annotator) {
      annotator.setTool({
        type: currentTool,
        color: currentColor,
        size: currentSize,
      });
    }
  }, [currentTool, currentColor, currentSize, annotator]);

  const handleSave = () => {
    if (!annotator || !canvasRef.current) return;

    const annotatedImage = canvasRef.current.toDataURL('image/png');
    const annotations = annotator.getAnnotations();
    
    onSave(annotatedImage, annotations);
    toast.success('Annotations saved');
  };

  const handleDownload = () => {
    if (!canvasRef.current) return;

    const link = document.createElement('a');
    link.download = `annotated-${Date.now()}.png`;
    link.href = canvasRef.current.toDataURL('image/png');
    link.click();
    
    toast.success('Image downloaded');
  };

  const handleUndo = () => {
    if (annotator) {
      annotator.undo();
    }
  };

  const handleClear = () => {
    if (annotator && confirm('Clear all annotations?')) {
      annotator.clear();
    }
  };

  const toolButtons = [
    { type: 'pen' as const, icon: Pen, label: 'Pen' },
    { type: 'highlighter' as const, icon: Highlighter, label: 'Highlighter' },
    { type: 'arrow' as const, icon: ArrowRight, label: 'Arrow' },
    { type: 'rectangle' as const, icon: Square, label: 'Rectangle' },
    { type: 'circle' as const, icon: Circle, label: 'Circle' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-gray-900">
      {/* Header */}
      <div className="bg-gray-800 text-white p-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold">Annotate Photo</h2>
        <div className="flex items-center space-x-2">
          <Button
            onClick={handleSave}
            size="sm"
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Save className="h-4 w-4 mr-1" />
            Save
          </Button>
          <Button
            onClick={onClose}
            size="sm"
            variant="outline"
            className="border-white text-white hover:bg-white/10"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="bg-gray-800 border-t border-gray-700 p-3 flex items-center space-x-3 overflow-x-auto">
        {/* Tools */}
        <div className="flex items-center space-x-2">
          {toolButtons.map(({ type, icon: Icon, label }) => (
            <button
              key={type}
              onClick={() => setCurrentTool(type)}
              className={`p-2 rounded-lg transition-colors ${
                currentTool === type
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
              title={label}
            >
              <Icon className="h-5 w-5" />
            </button>
          ))}
        </div>

        <div className="w-px h-8 bg-gray-700"></div>

        {/* Color Picker */}
        <div className="relative">
          <button
            onClick={() => setShowColorPicker(!showColorPicker)}
            className="flex items-center space-x-2 px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
          >
            <div
              className="w-6 h-6 rounded border-2 border-white"
              style={{ backgroundColor: currentColor }}
            ></div>
            <Palette className="h-4 w-4 text-gray-300" />
          </button>

          {showColorPicker && (
            <div className="absolute top-full left-0 mt-2 bg-gray-800 rounded-lg p-3 shadow-xl z-10">
              <div className="grid grid-cols-5 gap-2">
                {colors.map(color => (
                  <button
                    key={color}
                    onClick={() => {
                      setCurrentColor(color);
                      setShowColorPicker(false);
                    }}
                    className={`w-10 h-10 rounded border-2 transition-transform hover:scale-110 ${
                      currentColor === color ? 'border-blue-500 scale-110' : 'border-gray-600'
                    }`}
                    style={{ backgroundColor: color }}
                  ></button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Size Selector */}
        <div className="flex items-center space-x-2">
          <span className="text-gray-400 text-sm">Size:</span>
          <input
            type="range"
            min="1"
            max="20"
            value={currentSize}
            onChange={(e) => setCurrentSize(parseInt(e.target.value))}
            className="w-24"
          />
          <span className="text-white text-sm w-8">{currentSize}px</span>
        </div>

        <div className="w-px h-8 bg-gray-700"></div>

        {/* Actions */}
        <Button
          onClick={handleUndo}
          size="sm"
          variant="outline"
          className="border-gray-600 text-gray-300 hover:bg-gray-700"
        >
          <Undo className="h-4 w-4 mr-1" />
          Undo
        </Button>

        <Button
          onClick={handleClear}
          size="sm"
          variant="outline"
          className="border-gray-600 text-gray-300 hover:bg-gray-700"
        >
          <Trash2 className="h-4 w-4 mr-1" />
          Clear
        </Button>

        <Button
          onClick={handleDownload}
          size="sm"
          variant="outline"
          className="border-gray-600 text-gray-300 hover:bg-gray-700"
        >
          <Download className="h-4 w-4 mr-1" />
          Download
        </Button>
      </div>

      {/* Canvas Container */}
      <div
        ref={containerRef}
        className="flex-1 bg-gray-900 flex items-center justify-center overflow-hidden p-4"
      >
        <canvas
          ref={canvasRef}
          className="max-w-full max-h-full shadow-2xl"
          style={{ touchAction: 'none' }}
        />
      </div>

      {/* Footer Instructions */}
      <div className="bg-gray-800 border-t border-gray-700 p-3 text-center text-gray-400 text-sm">
        <p>Draw on the image to add annotations • Use touch or mouse to draw</p>
      </div>
    </div>
  );
}
